/**
 * News accordion: open/close articles, deep-link by hash.
 * Collapsed: whole article is clickable. Open: only the header toggles closed.
 */

import { prefersReducedMotion } from "../utils/reduced-motion";

const SCROLL_DURATION_MIN_MS = 280;
const SCROLL_DURATION_MAX_MS = 520;
const SCROLL_PX_PER_MS = 1.35;

let accordionAbort = null;
let scrollRafId = null;
let scrollInterruptAbort = null;
/** Hash we already scrolled to — avoids re-scrolling when load-more re-inits. */
let handledHashScroll = null;

function easeOutCubic(t) {
  return 1 - Math.pow(1 - t, 3);
}

function getScrollY() {
  return window.scrollY || document.documentElement.scrollTop || 0;
}

function getHeaderOffset() {
  const header = document.getElementById("header");
  const rootStyles = getComputedStyle(document.documentElement);
  const gap = parseFloat(rootStyles.getPropertyValue("--margin"));
  const gapPx = Number.isFinite(gap) ? gap : 10;

  if (header) {
    return Math.max(0, header.getBoundingClientRect().bottom + gapPx);
  }

  const headerHeight = parseFloat(rootStyles.getPropertyValue("--header-height")) || 0;
  return Math.max(0, headerHeight + gapPx);
}

function getArticleScrollTop(element) {
  return getScrollY() + element.getBoundingClientRect().top - getHeaderOffset();
}

function durationForDistance(distance) {
  return Math.min(
    SCROLL_DURATION_MAX_MS,
    Math.max(SCROLL_DURATION_MIN_MS, Math.abs(distance) / SCROLL_PX_PER_MS)
  );
}

function cancelArticleScroll() {
  if (scrollRafId !== null) {
    cancelAnimationFrame(scrollRafId);
    scrollRafId = null;
  }

  scrollInterruptAbort?.abort();
  scrollInterruptAbort = null;
}

function setScrollTop(y) {
  // Options form overrides CSS `scroll-behavior: smooth` on html,
  // which otherwise fights per-frame RAF updates.
  window.scrollTo({ top: y, left: 0, behavior: "auto" });
}

function watchScrollInterrupt() {
  scrollInterruptAbort?.abort();
  scrollInterruptAbort = new AbortController();
  const { signal } = scrollInterruptAbort;

  const stop = () => cancelArticleScroll();
  window.addEventListener("wheel", stop, { passive: true, signal });
  window.addEventListener("touchstart", stop, { passive: true, signal });
  window.addEventListener("keydown", (e) => {
    if (
      e.key === "ArrowUp" ||
      e.key === "ArrowDown" ||
      e.key === "PageUp" ||
      e.key === "PageDown" ||
      e.key === "Home" ||
      e.key === "End" ||
      e.key === " "
    ) {
      stop();
    }
  }, { signal });
}

function scrollToArticle(element) {
  cancelArticleScroll();

  const run = () => {
    const startY = getScrollY();
    const initialTargetY = Math.max(0, getArticleScrollTop(element));
    const distance = initialTargetY - startY;

    if (Math.abs(distance) < 1) {
      return;
    }

    if (prefersReducedMotion()) {
      setScrollTop(initialTargetY);
      return;
    }

    const duration = durationForDistance(distance);
    const startTime = performance.now();
    watchScrollInterrupt();

    function tick(now) {
      const t = Math.min(1, (now - startTime) / duration);
      // Re-measure every frame: logo collapse changes header offset mid-scroll.
      const targetY = Math.max(0, getArticleScrollTop(element));
      setScrollTop(startY + (targetY - startY) * easeOutCubic(t));

      if (t < 1) {
        scrollRafId = requestAnimationFrame(tick);
        return;
      }

      setScrollTop(Math.max(0, getArticleScrollTop(element)));
      scrollRafId = null;
      scrollInterruptAbort?.abort();
      scrollInterruptAbort = null;
    }

    scrollRafId = requestAnimationFrame(tick);
  };

  // Wait a frame so open-panel layout is applied before measuring.
  scrollRafId = requestAnimationFrame(() => {
    scrollRafId = requestAnimationFrame(run);
  });
}

function setArticleState(article, isOpen) {
  const header = article.querySelector(".c-news-article__header");
  const body = article.querySelector(".c-news-article__body");

  article.setAttribute("data-state", isOpen ? "open" : "closed");

  if (header) {
    header.setAttribute("aria-expanded", isOpen ? "true" : "false");
  }

  if (body) {
    body.setAttribute("aria-hidden", isOpen ? "false" : "true");
  }
}

function openArticle(article, updateHash = true) {
  setArticleState(article, true);

  if (updateHash && article.id) {
    const nextUrl = `${window.location.pathname}${window.location.search}#${article.id}`;
    history.replaceState(null, "", nextUrl);
  }
}

function toggleArticle(article) {
  const isOpen = article.getAttribute("data-state") === "open";

  if (isOpen) {
    setArticleState(article, false);

    if (article.id && window.location.hash === `#${article.id}`) {
      history.replaceState(
        null,
        "",
        `${window.location.pathname}${window.location.search}`
      );
    }

    handledHashScroll = null;
    return;
  }

  openArticle(article, true);
  // Mark hash as handled so load-more re-inits don't scroll again.
  if (article.id) {
    handledHashScroll = article.id;
  }
  scrollToArticle(article);
}

export function resetNewsAccordionHashScroll() {
  handledHashScroll = null;
  cancelArticleScroll();
}

export function initNewsAccordion({ handleHash = true } = {}) {
  const articles = document.querySelectorAll(".js-news-article");

  if (articles.length === 0) {
    return;
  }

  accordionAbort?.abort();
  accordionAbort = new AbortController();
  const { signal } = accordionAbort;

  articles.forEach((article) => {
    article.addEventListener(
      "click",
      (event) => {
        // Let real links work (e.g. inside open body text).
        if (event.target.closest("a[href]")) {
          return;
        }

        const isOpen = article.getAttribute("data-state") === "open";

        // Open: only the title/toggle row collapses — not the body.
        if (isOpen && !event.target.closest(".c-news-article__header")) {
          return;
        }

        event.preventDefault();
        toggleArticle(article);
      },
      { signal }
    );
  });

  if (!handleHash) {
    return;
  }

  const hash = window.location.hash.replace(/^#/, "");

  if (!hash) {
    handledHashScroll = null;
    return;
  }

  // Already scrolled for this hash (click open, or earlier deep-link).
  if (handledHashScroll === hash) {
    return;
  }

  const target = document.getElementById(hash);

  if (target && target.classList.contains("js-news-article")) {
    handledHashScroll = hash;
    openArticle(target, false);
    scrollToArticle(target);
  }
}
